import { useEffect, useState } from 'react';
import {
  IconFlame,
  IconRefresh,
  IconCalendar,
  IconMapPin,
  IconTrendingUp,
  IconAlertTriangle,
  IconInfoCircle,
  IconChevronDown,
  IconChevronUp,
} from '@tabler/icons-react';
import {
  getTodayForecast,
  getTomorrowForecast,
  triggerFireRiskProcessing,
  FireRiskForecast,
  FireRisk,
  getRiskLevelInfo,
  getFireRiskStats,
} from '../services/fireRiskService';

type SortField = 'countyName' | 'rcm';
type SortDirection = 'asc' | 'desc';

export default function FireRiskPage() {
  const [todayForecast, setTodayForecast] = useState<FireRiskForecast | null>(null);
  const [tomorrowForecast, setTomorrowForecast] = useState<FireRiskForecast | null>(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'today' | 'tomorrow'>('today');
  const [sortField, setSortField] = useState<SortField>('rcm');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [today, tomorrow] = await Promise.all([getTodayForecast(), getTomorrowForecast()]);
      setTodayForecast(today);
      setTomorrowForecast(tomorrow);
    } catch (err) {
      console.error('Error loading fire risk data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load fire risk data');
    } finally {
      setLoading(false);
    }
  };

  const handleTriggerProcessing = async () => {
    try {
      setProcessing(true);
      setError(null);
      setSuccessMessage(null);
      const result = await triggerFireRiskProcessing();
      if (result.success) {
        setSuccessMessage(result.message);
        // Reload data after processing
        await loadData();
      } else {
        setError(result.message);
      }
    } catch (err) {
      console.error('Error triggering processing:', err);
      setError(err instanceof Error ? err.message : 'Failed to trigger processing');
    } finally {
      setProcessing(false);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getSortedMunicipalities = (forecast: FireRiskForecast | null): FireRisk[] => {
    if (!forecast || !forecast.forecast) return [];

    let municipalities = Object.values(forecast.forecast);

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      municipalities = municipalities.filter(
        mun =>
          mun.countyName.toLowerCase().includes(query) ||
          mun.code.includes(query) ||
          mun.countyId.includes(query)
      );
    }

    // Sort
    municipalities.sort((a, b) => {
      let compareValue = 0;
      if (sortField === 'countyName') {
        compareValue = a.countyName.localeCompare(b.countyName);
      } else if (sortField === 'rcm') {
        compareValue = a.rcm - b.rcm;
      }
      return sortDirection === 'asc' ? compareValue : -compareValue;
    });

    return municipalities;
  };

  const currentForecast = activeTab === 'today' ? todayForecast : tomorrowForecast;
  const municipalities = getSortedMunicipalities(currentForecast);
  const stats = getFireRiskStats(currentForecast);

  if (loading) {
    return (
      <div className="page-header d-print-none">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status"></div>
          <div className="text-muted mt-2">Loading fire risk data...</div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Page Header */}
      <div className="page-header d-print-none">
        <div className="row g-2 align-items-center">
          <div className="col">
            <div className="page-pretitle">Maps</div>
            <h2 className="page-title">Fire Risk Forecast</h2>
          </div>
          <div className="col-auto ms-auto d-print-none">
            <div className="d-flex gap-2">
              <button
                onClick={handleTriggerProcessing}
                className="btn btn-primary"
                disabled={processing}
              >
                {processing ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Processing...
                  </>
                ) : (
                  <>
                    <IconRefresh className="icon me-2" />
                    Update Fire Risk Data
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Page Body */}
      <div className="page-body">
        {/* Alerts */}
        {error && (
          <div className="alert alert-danger alert-dismissible" role="alert">
            <div className="d-flex">
              <IconAlertTriangle className="icon alert-icon" />
              <div>{error}</div>
            </div>
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
              aria-label="Close"
            ></button>
          </div>
        )}

        {successMessage && (
          <div className="alert alert-success alert-dismissible" role="alert">
            <div className="d-flex">
              <IconInfoCircle className="icon alert-icon" />
              <div>{successMessage}</div>
            </div>
            <button
              type="button"
              className="btn-close"
              onClick={() => setSuccessMessage(null)}
              aria-label="Close"
            ></button>
          </div>
        )}

        {/* Info Card */}
        <div className="card mb-3">
          <div className="card-body">
            <div className="row">
              <div className="col-md-8">
                <h3 className="card-title">About Fire Risk Data</h3>
                <p className="text-muted mb-0">
                  Fire risk forecasts are provided by IPMA (Instituto Português do Mar e da
                  Atmosfera) and updated daily. The data includes risk levels for all Portuguese
                  municipalities on a scale from 1 (Low) to 5 (Maximum).
                </p>
              </div>
              <div className="col-md-4">
                <div className="text-muted small">
                  <div>
                    <strong>Scheduled Update:</strong> Daily at 11:00 UTC
                  </div>
                  <div>
                    <strong>Data Source:</strong> IPMA Open Data API
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        {currentForecast && (
          <div className="row row-cards mb-3">
            <div className="col-sm-6 col-lg-3">
              <div className="card card-sm">
                <div className="card-body">
                  <div className="row align-items-center">
                    <div className="col-auto">
                      <span className="bg-primary text-white avatar">
                        <IconMapPin className="icon" />
                      </span>
                    </div>
                    <div className="col">
                      <div className="font-weight-medium">{stats.total} Municipalities</div>
                      <div className="text-muted">Total Coverage</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {stats.byLevel.map(({ level, count, label }) => {
              const info = getRiskLevelInfo(level);
              return (
                <div key={level} className="col-sm-6 col-lg-3">
                  <div className="card card-sm">
                    <div className="card-body">
                      <div className="row align-items-center">
                        <div className="col-auto">
                          <span className={`${info.badge} text-white avatar`}>
                            <IconFlame className="icon" />
                          </span>
                        </div>
                        <div className="col">
                          <div className="font-weight-medium">
                            {count} {label}
                          </div>
                          <div className="text-muted">Risk Level {level}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Main Card with Tabs */}
        <div className="card">
          <div className="card-header">
            <ul className="nav nav-tabs card-header-tabs" data-bs-toggle="tabs" role="tablist">
              <li className="nav-item" role="presentation">
                <a
                  href="#tabs-today"
                  className={`nav-link ${activeTab === 'today' ? 'active' : ''}`}
                  onClick={e => {
                    e.preventDefault();
                    setActiveTab('today');
                  }}
                  role="tab"
                >
                  <IconCalendar className="icon me-2" />
                  Today
                  {todayForecast && (
                    <span className="badge bg-primary text-white ms-2">
                      {Object.keys(todayForecast.forecast).length}
                    </span>
                  )}
                </a>
              </li>
              <li className="nav-item" role="presentation">
                <a
                  href="#tabs-tomorrow"
                  className={`nav-link ${activeTab === 'tomorrow' ? 'active' : ''}`}
                  onClick={e => {
                    e.preventDefault();
                    setActiveTab('tomorrow');
                  }}
                  role="tab"
                >
                  <IconTrendingUp className="icon me-2" />
                  Tomorrow
                  {tomorrowForecast && (
                    <span className="badge bg-primary text-white ms-2">
                      {Object.keys(tomorrowForecast.forecast).length}
                    </span>
                  )}
                </a>
              </li>
            </ul>
          </div>

          <div className="card-body">
            {/* Forecast Metadata */}
            {currentForecast && (
              <div className="row mb-3">
                <div className="col-md-4">
                  <div className="text-muted small">
                    <strong>Forecast Date:</strong> {currentForecast.forecastDate}
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="text-muted small">
                    <strong>Execution Date:</strong> {currentForecast.executionDate}
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="text-muted small">
                    <strong>File Date:</strong>{' '}
                    {new Date(currentForecast.fileDate).toLocaleString()}
                  </div>
                </div>
              </div>
            )}

            {/* Search and Filters */}
            <div className="row mb-3">
              <div className="col-md-6">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by municipality name or code..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                />
              </div>
              <div className="col-md-6 text-end">
                <span className="text-muted">
                  Showing {municipalities.length} of {stats.total} municipalities
                </span>
              </div>
            </div>

            {/* Table */}
            <div className="table-responsive">
              <table className="table table-vcenter card-table table-striped">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('countyName')}>
                      Municipality
                      {sortField === 'countyName' && (
                        <>
                          {sortDirection === 'asc' ? (
                            <IconChevronUp className="icon ms-1" />
                          ) : (
                            <IconChevronDown className="icon ms-1" />
                          )}
                        </>
                      )}
                    </th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('rcm')}>
                      Risk Level
                      {sortField === 'rcm' && (
                        <>
                          {sortDirection === 'asc' ? (
                            <IconChevronUp className="icon ms-1" />
                          ) : (
                            <IconChevronDown className="icon ms-1" />
                          )}
                        </>
                      )}
                    </th>
                    <th>Coordinates</th>
                  </tr>
                </thead>
                <tbody>
                  {municipalities.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center text-muted py-5">
                        No data available
                      </td>
                    </tr>
                  ) : (
                    municipalities.map(mun => {
                      const riskInfo = getRiskLevelInfo(mun.rcm);
                      return (
                        <tr key={mun.code}>
                          <td>
                            <span className="text-muted">{mun.code}</span>
                          </td>
                          <td>
                            <strong>{mun.countyName}</strong>
                            <div className="text-muted small">{mun.countyId}</div>
                          </td>
                          <td>
                            <span className={`badge text-white ${riskInfo.badge}`}>
                              {mun.rcm} - {riskInfo.label}
                            </span>
                          </td>
                          <td className="text-muted small">
                            {mun.latitude.toFixed(4)}, {mun.longitude.toFixed(4)}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
